/**
 * Tile do participante: vídeo (ou a inicial, com a câmera desligada), nome, microfone desligado,
 * conexão ruim e, enquanto a pessoa fala, um anel ciano de 3px em volta do card. `TileView` é só
 * visual; `RemoteTile` liga os hooks do LiveKit.
 */
import { isTrackReference, useConnectionQualityIndicator, useIsMuted, useIsSpeaking, VideoTrack } from '@livekit/components-react';
import { Track } from 'livekit-client';
import { MicOff, WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { displayNameOf, initialsOf, toneFor } from './participantDisplay';

const AVATAR = Object.freeze({
    lg: 'h-28 w-28 text-4xl',
    md: 'h-20 w-20 text-2xl',
    sm: 'h-14 w-14 text-lg',
    xs: 'h-9 w-9 text-xs',
});

const CHIP = Object.freeze({
    lg: 'bottom-3 left-3 px-2.5 py-1 text-sm',
    md: 'bottom-3 left-3 px-2.5 py-1 text-sm',
    sm: 'bottom-2 left-2 px-2 py-0.5 text-xs',
    xs: 'bottom-1.5 left-1.5 px-1.5 py-0.5 text-[11px]',
});

/** Três barras (ruim = uma acesa) ou, sem conexão, o ícone de wi-fi cortado. */
function QualityGlyph({ quality, small }) {
    if (quality === 'lost') return <WifiOff className={small ? 'h-3 w-3' : 'h-4 w-4'} />;
    return (
        <span className={cn('flex items-end gap-[2px]', small && 'scale-75')} aria-hidden="true">
            <i className="block h-1.5 w-[3px] rounded-sm bg-amber-300" />
            <i className="block h-2.5 w-[3px] rounded-sm bg-white/35" />
            <i className="block h-3.5 w-[3px] rounded-sm bg-white/35" />
        </span>
    );
}

/**
 * @param {{
 *   name: string, initials: string, tone?: string, cameraOn: boolean, muted?: boolean, speaking?: boolean,
 *   quality?: string, size?: 'lg'|'md'|'sm'|'xs', label?: boolean, labelClassName?: string, className?: string,
 *   children?: React.ReactNode,
 * }} props: `children` é o elemento de vídeo; só aparece com a câmera ligada. `speaking` acende o anel
 *   ciano. `labelClassName` reposiciona o nome (ex.: acima da barra que flutua sobre o vídeo no celular).
 */
export function TileView({ name, initials, tone = 'bg-violet-700', cameraOn, muted = false, speaking = false, quality = 'good', size = 'lg', label = true, labelClassName, className, children }) {
    const small = size === 'xs';
    const poor = quality === 'poor' || quality === 'lost';
    return (
        <div className={cn('relative overflow-hidden bg-neutral-900', className)}>
            {cameraOn ? children : (
                <div className="absolute inset-0 flex items-center justify-center bg-neutral-800">
                    <span aria-hidden="true" className={cn('flex items-center justify-center rounded-full font-semibold text-white', tone, AVATAR[size])}>
                        {initials}
                    </span>
                </div>
            )}
            {/* Por cima do vídeo, por dentro da borda (o palco é cortado pela janela). Sempre no DOM, só a
                opacidade muda: assim o anel aparece e some suave em vez de piscar a cada pausa. */}
            <span
                aria-hidden="true"
                data-speaking={speaking ? 'true' : 'false'}
                className={cn(
                    'pointer-events-none absolute inset-0 rounded-[inherit] ring-[3px] ring-inset ring-cyan-400 shadow-[inset_0_0_22px_rgba(34,211,238,0.35)]',
                    'transition-opacity duration-200 ease-out motion-reduce:transition-none',
                    speaking ? 'opacity-100' : 'opacity-0',
                )}
            />
            {poor && (
                <span
                    role="img"
                    aria-label={quality === 'lost' ? 'Conexão perdida' : 'Conexão ruim'}
                    className={cn(
                        'absolute inline-flex items-center justify-center rounded-full bg-black/60',
                        small ? 'right-1.5 top-1.5 h-5 w-5' : 'right-3 top-3 h-8 w-8',
                        quality === 'lost' ? 'text-red-400' : 'text-amber-300',
                    )}
                >
                    <QualityGlyph quality={quality} small={small} />
                </span>
            )}
            {label && (
                <div className={cn('absolute inline-flex max-w-[78%] items-center gap-1.5 rounded-lg bg-black/60 font-medium text-white', CHIP[size], labelClassName)}>
                    {muted && <MicOff role="img" aria-label="Microfone desligado" className={cn('shrink-0 text-red-400', small ? 'h-3 w-3' : 'h-3.5 w-3.5')} />}
                    <span className="truncate">{name}</span>
                </div>
            )}
        </div>
    );
}

/**
 * Tile do outro participante, ocupando todo o palco (ou a célula da grade). `fit`: `contain` mostra o quadro inteiro (o 1:1);
 * `cover` preenche a célula cortando as bordas, como na galeria de uma sala com várias pessoas.
 */
export function RemoteTile({ trackRef, size = 'lg', labelClassName, fit = 'contain' }) {
    const { participant } = trackRef;
    const muted = useIsMuted({ participant, source: Track.Source.Microphone });
    // Ao desligar a câmera o LiveKit só silencia a trilha: ela continua publicada (e o vídeo vira
    // um quadro preto). `useIsMuted` vale também para câmera nunca publicada.
    const cameraMuted = useIsMuted({ participant, source: Track.Source.Camera });
    const { quality } = useConnectionQualityIndicator({ participant });
    const speaking = useIsSpeaking(participant);
    const name = displayNameOf(participant, 'Contato');
    return (
        <TileView
            name={name}
            initials={initialsOf(name)}
            tone={toneFor(participant.identity ?? name)}
            cameraOn={isTrackReference(trackRef) && !cameraMuted}
            muted={muted}
            speaking={speaking}
            quality={quality}
            size={size}
            labelClassName={labelClassName}
            className="h-full w-full rounded-[inherit]"
        >
            <VideoTrack trackRef={trackRef} data-remote-video="" className={cn('absolute inset-0 h-full w-full', fit === 'cover' ? 'object-cover' : 'object-contain')} />
        </TileView>
    );
}
